<template>
  <section
    class="tw-flex tw-h-screen tw-items-center tw-justify-center tw-text-center"
  >
    <cl-spinner :loading-text="$t('loading')" :size="'medium'" class="mr-2" />

    <h1>Redirecting via SAML</h1>
  </section>
</template>

<script setup lang="ts">
import { onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useAuthenticationStore } from "@/store/authentication.store";
import { ACCOUNT_TYPE } from "@/constants";
import { isAxiosError } from "axios";
import { useAuthorisationStrategy } from "@/authorisation";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";
import { getApiClientId, getApiClientUrl } from "@/config/runtimeConfig";

const route = useRoute();
const router = useRouter();
const authStore = useAuthenticationStore();
const selectedAccount = computed(() => authStore.selectedAccount);

const { authoriseOnSignIn, clearAuthorisationCache } =
  useAuthorisationStrategy();

const hasAccessToOrganisation = (
  userType: string,
  userOrgUuid: string | null,
  targetOrgUuid: string,
): boolean => {
  // System admins have access to all organizations
  if (userType === ACCOUNT_TYPE.SYSTEM) {
    return true;
  }

  // For other roles, check if they belong to the target organization
  return userOrgUuid === targetOrgUuid;
};

onMounted(async () => {
  if (route.query.token) {
    try {
      const response = await axios.post(
        `${getApiClientUrl()}/v1/idp/oauth/token`,
        {
          grant_type: "refresh_token",
          client_id: getApiClientId(),
          refresh_token: route.query.token,
        },
      );

      if (response.data) {
        authStore.ssoToken(response.data);

        await authoriseOnSignIn();

        const state = route.query.state;
        const redirectKey = state ? `auth_redirect_${state}` : "auth_redirect";
        const savedRedirect = sessionStorage.getItem(redirectKey);

        if (savedRedirect) {
          sessionStorage.removeItem(redirectKey);

          const userType = selectedAccount.value?.type_name || "";

          // For system admins, use the original redirect URL without modification
          if (userType === ACCOUNT_TYPE.SYSTEM) {
            router.push(savedRedirect);
          } else {
            // For other users, validate access to redirect organization
            const redirectOrgMatch = savedRedirect.match(
              /\/(msp|customer|system)\/([^/?#]+)/,
            );
            if (redirectOrgMatch) {
              const redirectOrgUuid = redirectOrgMatch[2];
              const userOrgUuid = selectedAccount.value?.uuid || null;

              if (
                hasAccessToOrganisation(userType, userOrgUuid, redirectOrgUuid)
              ) {
                router.push(savedRedirect);
              } else {
                routeBasedOnRole();
              }
            } else {
              router.push(savedRedirect);
            }
          }
        } else {
          routeBasedOnRole();
        }

        // router.push("/auth/sign");
      }
    } catch (e: unknown) {
      clearAuthorisationCache();
      authStore.clearAuthCache();

      if (isAxiosError(e)) {
        const error = e.response?.data.error || "Looks like we have an ERROR";

        router.push({
          path: "/auth/sign",
          query: { error },
        });
      }
    }
  }
});

const routeBasedOnRole = async () => {
  const type = selectedAccount.value?.type_name;
  const orgUuid = selectedAccount.value?.uuid;

  if (type === ACCOUNT_TYPE.SYSTEM) {
    router.push(buildOrgUrl("system", orgUuid));
    return;
  }

  if (type === ACCOUNT_TYPE.MSP) {
    router.push(buildOrgUrl("msp", orgUuid));
    return;
  }

  if (type === ACCOUNT_TYPE.CUSTOMER) {
    router.push(buildOrgUrl("customer", orgUuid));
    return;
  }
  router.push(buildOrgUrl("system", orgUuid));
};
</script>
